import { useId, useMemo, useState, type FormEvent } from 'react';

import useDashboardWidgetMutations from '@/features/dashboard/hooks/useDashboardWidgetMutations';
import type { WidgetId } from '@/features/dashboard/registry';
import useCurrentHome from '@/features/homeAutomation/hooks/useCurrentHome';
import useSensors from '@/features/homeAutomation/hooks/useSensors';
import { cn } from '@/lib/cn';
import { errorInputClasses, inputClasses } from '@/lib/formStyles';

import { DEFAULT_SENSORS_CONFIG, mergeSensorsConfig, POLL_SECONDS_MAX, POLL_SECONDS_MIN, type SensorsWidgetConfig } from './config';

export default function SensorsSettingsForm({
  widgetId,
  config,
  onSaved,
  formId,
}: {
  widgetId: WidgetId;
  config: Record<string, unknown>;
  onSaved: () => void;
  formId: string;
}) {
  const mutations = useDashboardWidgetMutations();
  const initial = mergeSensorsConfig(config as Partial<SensorsWidgetConfig>);

  const homeQuery = useCurrentHome();
  const currentHomeId = homeQuery.data?.id ?? null;

  // Single-home v1: the only selectable home is the user's current home. The
  // "no homes" branch is reachable if the home query fails or is loading.
  const homeOptions = useMemo(
    () => (currentHomeId ? [{ id: currentHomeId, name: homeQuery.data!.name }] : []),
    [currentHomeId, homeQuery.data],
  );

  const [homeId, setHomeId] = useState<string | null>(initial.homeId ?? currentHomeId);
  const [selectedIds, setSelectedIds] = useState<string[]>(initial.sensorIds);
  const [pollRaw, setPollRaw] = useState(String(initial.pollSeconds));

  const effectiveHomeId = homeId ?? currentHomeId;
  const sensorsQuery = useSensors(effectiveHomeId ?? undefined);
  const allSensors = sensorsQuery.data ?? [];

  const pollNum = Number(pollRaw);
  const pollValid = Number.isFinite(pollNum) && Number.isInteger(pollNum) && pollNum >= POLL_SECONDS_MIN && pollNum <= POLL_SECONDS_MAX;
  const noHome = !effectiveHomeId;
  const canSave = !noHome && pollValid && !mutations.isPending;

  const homeSelectId = useId();
  const pollId = useId();
  const pollErrId = useId();

  const toggleSensor = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSave || !effectiveHomeId) return;
    // Drop any selected ids that no longer exist in this home.
    const known = new Set(allSensors.map((s) => s.id));
    const cleanedIds = selectedIds.filter((id) => known.has(id));
    const values: SensorsWidgetConfig = {
      homeId: effectiveHomeId,
      sensorIds: cleanedIds,
      pollSeconds: pollNum,
    };
    mutations.updateConfig(widgetId, values as unknown as Record<string, unknown>);
    onSaved();
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      {noHome ? (
        <div className="rounded-lg border border-dashed border-(--border-default) bg-(--bg-surface) px-4 py-6 text-sm text-(--text-secondary)">
          <p className="font-medium text-(--text-primary)">No home configured.</p>
          <p className="mt-1">
            Open <em>Home settings</em> from your user menu to create one first, then come back here.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={homeSelectId} className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">
              Home
            </label>
            <select
              id={homeSelectId}
              value={homeId ?? currentHomeId ?? ''}
              onChange={(e) => setHomeId(e.target.value || null)}
              className={inputClasses}
            >
              {homeOptions.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">Sensors</legend>
            <p className="text-xs text-(--text-tertiary)">Leave all unchecked to show every sensor in the home.</p>
            {sensorsQuery.isPending ? (
              <div className="text-xs text-(--text-tertiary)">Loading sensors…</div>
            ) : sensorsQuery.isError ? (
              <div className="text-xs text-(--accent-danger)">Failed to load sensors.</div>
            ) : allSensors.length === 0 ? (
              <div className="text-xs text-(--text-tertiary)">No sensors in this home yet.</div>
            ) : (
              <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-md border border-(--border-subtle) bg-(--bg-surface) p-2">
                {allSensors.map((s) => {
                  const checked = selectedIds.includes(s.id);
                  return (
                    <li key={s.id}>
                      <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-(--text-primary) hover:bg-(--bg-surface-hover)">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSensor(s.id)}
                          className="h-4 w-4 rounded border-(--border-default) accent-(--accent-primary)"
                        />
                        <span className="min-w-0 flex-1 truncate">{s.name}</span>
                        {s.location && <span className="truncate text-xs text-(--text-tertiary)">{s.location}</span>}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </fieldset>

          <div className="flex flex-col gap-1.5">
            <label htmlFor={pollId} className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">
              Refresh interval (seconds)
            </label>
            <input
              id={pollId}
              type="number"
              min={POLL_SECONDS_MIN}
              max={POLL_SECONDS_MAX}
              step={1}
              required
              value={pollRaw}
              onChange={(e) => setPollRaw(e.target.value)}
              aria-invalid={!pollValid || undefined}
              aria-describedby={!pollValid ? pollErrId : undefined}
              className={cn(inputClasses, !pollValid && errorInputClasses)}
            />
            {!pollValid && (
              <p id={pollErrId} className="text-xs text-(--accent-danger)">
                Must be an integer between {POLL_SECONDS_MIN} and {POLL_SECONDS_MAX}.
              </p>
            )}
            <p className="text-xs text-(--text-tertiary)">Default {DEFAULT_SENSORS_CONFIG.pollSeconds}s.</p>
          </div>
        </>
      )}
    </form>
  );
}
